import { useEffect, useMemo, useRef } from "react";
import { Card, Flex, Text } from "@jsq/ds/core";
import { useFormContext } from "@jsq/ds/forms";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { useGuidedProfileCreation } from "~/pages/Compliance/flags";
import { isJointAccountType } from "~/pages/Compliance/utils/accountTypeUtils";
import { extractChildProfiles } from "../../../utils";
import AIIcon from "../AIIcon";
import { DocumentDetailsFormValues, type CaseInfo } from "../DocumentDetailsFormValues";
import { ProfileDetailsFormValues } from "../ProfileDetailsFormValues";
import {
  aiIconContainer,
  documentDetailsContainer,
  infoBox,
  infoText,
  profileDetailsCard,
  profileDetailsContainer,
} from "../styles_vanilla.css";
import { type DocumentType, type FormValues } from "../types";

type Props = {
  complianceCase: GetComplianceCaseQuery["complianceCase"] | undefined;
  picrRevampEnabled: boolean;
  readonly: boolean;
  isExtracting: boolean;
  filteredDocument: DocumentType | undefined;
  documentId: number;
  showProfileDetails: boolean;
  showAIIndication: string;
  isPICR: boolean;
  caseInfo: CaseInfo | undefined;
};

export const ProfileAwareFormContent = ({
  complianceCase,
  picrRevampEnabled,
  readonly,
  isExtracting,
  filteredDocument,
  documentId,
  showProfileDetails,
  showAIIndication,
  isPICR,
  caseInfo,
}: Props) => {
  const { values, setFieldValue } = useFormContext<FormValues>();
  const guidedProfileCreation = useGuidedProfileCreation();

  const allProfiles = useMemo(
    () => extractChildProfiles(complianceCase, picrRevampEnabled),
    [complianceCase, picrRevampEnabled],
  );

  const isProfileSelected = guidedProfileCreation ? Boolean(values.relatedProfile) : true;
  const selectedProfile = allProfiles.find((p) => String(p?.id) === values.relatedProfile);
  const shouldApplyJointRestrictions =
    guidedProfileCreation && isJointAccountType(selectedProfile?.accountType);

  const prevRelatedProfileRef = useRef<string>("");

  useEffect(() => {
    const prev = prevRelatedProfileRef.current;
    const curr = values.relatedProfile;
    prevRelatedProfileRef.current = curr;

    if (!guidedProfileCreation || !prev || prev === curr) return;

    const wasJoint = isJointAccountType(
      allProfiles.find((p) => String(p?.id) === prev)?.accountType,
    );
    const isNowJoint = isJointAccountType(
      allProfiles.find((p) => String(p?.id) === curr)?.accountType,
    );

    if (wasJoint !== isNowJoint) {
      void setFieldValue("documentType", undefined);
      void setFieldValue("secondaryDocumentType", "");
      void setFieldValue("documentStatus", "");
      void setFieldValue("isCertifiedDocument", false);
      void setFieldValue("description", "");
    }
  }, [values.relatedProfile, guidedProfileCreation, allProfiles, setFieldValue]);

  return (
    <>
      {showProfileDetails && (
        <div className={profileDetailsContainer}>
          <div className={showAIIndication} data-testid="ai-border-container"></div>
          {showAIIndication !== "" && (
            <div className={aiIconContainer}>
              <AIIcon />
            </div>
          )}
          <Card className={profileDetailsCard}>
            <Text marginBottom="lg" variant="secondary-md">
              PROFILE DETAILS
            </Text>
            <ProfileDetailsFormValues
              complianceCase={complianceCase}
              readonly={readonly}
              isExtracting={isExtracting}
              extractedData={filteredDocument?.agentExtractedData}
              extractionStatus={
                filteredDocument?.agentJobId ? filteredDocument?.agentJobStatus : undefined
              }
              conflictsResolved={filteredDocument?.conflictsResolved}
              documentName={filteredDocument?.name}
              documentId={documentId}
            />
          </Card>
        </div>
      )}
      <div className={documentDetailsContainer}>
        <div className={showAIIndication} data-testid="ai-border-container"></div>
        {showAIIndication !== "" && (
          <div className={aiIconContainer}>
            <AIIcon />
          </div>
        )}
        <Card>
          <Text marginBottom="lg" variant="secondary-md">
            DOCUMENT DETAILS
          </Text>
          {isPICR && !picrRevampEnabled && (
            <Flex flexDirection="column" className={infoBox}>
              <Text variant="regular-md" className={infoText}>
                This document will be saved to all payment beneficiary profiles within this
                case.
              </Text>
            </Flex>
          )}
          <DocumentDetailsFormValues
            readonly={readonly}
            isExtracting={isExtracting}
            caseInfo={caseInfo}
            documentName={filteredDocument?.name}
            documentId={documentId}
            isProfileSelected={isProfileSelected}
            shouldApplyJointRestrictions={shouldApplyJointRestrictions}
          />
        </Card>
      </div>
    </>
  );
};
